import { filter, find, map, size, toLower } from 'lodash';
import * as React from 'react';
import { ReactChild } from 'react';
import { SearchFilterValueTypes } from '../../constants/visibilityFilters';
import { MacroInputApi } from './MacroInput';
import { BaseOption } from './types/BaseOption';
import { MacroParts } from './types/MacroParts';

export const OPTIONS_LIMIT = 5;

export interface MacroOption extends BaseOption {
  macro?: MacroParts;
}
export type MacroOptions = MacroOption[];

export type MacroOptionsGroup = {
  key: string;
  label: ReactChild;
  options: MacroOptions;
};

export type MacroOptionsOrGroups = (MacroOption | MacroOptionsGroup)[];

const getGlobalOptions = (availableFilters: Record<string, any> = {}): MacroOptions => {
  return Object.values(availableFilters).map(({ type, label, description }) => ({
    key: type,
    label,
    description,
    macro: { token: type, operator: '', value: '' },
  }));
};

export const getDefaultOptions = (
  availableFilters: Record<string, any> = {},
): MacroOptionsOrGroups => [
  {
    key: 'default-options',
    label: 'Keys',
    options: getGlobalOptions(availableFilters),
  },
];

export const getResourceOptions = (
  resourceSuggestions: Record<string, any> = {},
  getSourceMeta?: (id: string) => void,
): MacroOptionsOrGroups => {
  const options: MacroOptionsOrGroups = [];

  // resource suggestions
  if (resourceSuggestions.lastUpdated && resourceSuggestions.totalItems > 0) {
    const resourceOptions = map(resourceSuggestions.items, ({ id, name }) => ({
      key: id,
      label: name,
      meta: getSourceMeta?.(id),
      isResourceMatch: true,
    }));

    if (resourceSuggestions.totalItems > size(resourceSuggestions.items)) {
      resourceOptions.push({
        key: 'show-all-results',
        label: 'View Search Results',
      });
    }

    options.push({
      key: 'resources',
      label: 'Top Recommendations',
      options: resourceOptions,
    });
  }

  return options;
};

const getOptionsLimit = (macro: { token: string }, availableFilters: Record<string, any>) => {
  if (macro?.token === 'flow_type') {
    const data = availableFilters?.['flow_type'];
    const allFlowsTypes = data?.availableValues;
    return allFlowsTypes?.length ?? OPTIONS_LIMIT + 1;
  }

  return OPTIONS_LIMIT + 1;
};

export const getStaticOptions = (args: {
  inputApi: MacroInputApi;
  availableFilters: Record<string, any>;
}): MacroOptionsOrGroups => {
  const { inputApi, availableFilters } = args;

  const currentText = inputApi.getCurrentText();
  const currentMacro = inputApi.getCurrentMacro();

  if (currentMacro) {
    const { type, label, availableValues } = availableFilters[currentMacro.token] || {};
    const hasFixedValues = Array.isArray(availableValues);

    if (hasFixedValues) {
      const options: MacroOptions = [];
      const macroValueLower = toLower(currentMacro.value);
      const selectedValue = find(availableValues, {
        value: currentMacro.value,
      });

      const limit = getOptionsLimit(currentMacro, availableFilters);

      /**
       * When a user selects a flow type (e.g., flow_type:cdc), they can reposition their cursor
       * anywhere within the filter text and still be able to select a different flow type option.
       * This provides a seamless selecting experience without requiring the user to delete and retype
       * the entire filter.
       */
      const filteredAvailableValues = filter(availableValues, (availableValue) => {
        if (!currentMacro.value) {
          return true;
        }

        // if an available value is selected, it should not be listed
        if (selectedValue) {
          return availableValue.value !== selectedValue.value;
        }

        return (
          toLower(availableValue.label).includes(macroValueLower) ||
          toLower(availableValue.value).includes(macroValueLower)
        );
      }).slice(0, limit);

      for (const valueItem of filteredAvailableValues) {
        const { value } = valueItem;
        const effectiveValue = quoteValue(value);

        options.push({
          key: effectiveValue,
          label: effectiveValue,
          macro: { ...currentMacro, value: effectiveValue },
        });
      }

      return [
        {
          key: type,
          label,
          options,
        },
      ];
    } else {
      return [];
    }
  }

  const macroOptions: MacroOptions = [];
  const searchOptions: MacroOptions = [];
  const inputLower = currentText.toLowerCase();
  const globalOptions = getGlobalOptions(availableFilters);

  for (const option of globalOptions) {
    const { type, label, description, availableValues } = availableFilters[option.key];

    if (type.startsWith(inputLower)) {
      if (macroOptions.length === OPTIONS_LIMIT) {
        continue;
      }

      macroOptions.push({
        key: `${option.key}-${type}`,
        label,
        description,
        macro: { token: type, operator: '', value: ' ' },
      });
    }

    if (availableValues) {
      availableValues.forEach(({ label, value }) => {
        if (
          macroOptions.length < OPTIONS_LIMIT &&
          (toLower(label).includes(inputLower) || toLower(value).includes(inputLower))
        ) {
          const effectiveValue = quoteValue(value);
          macroOptions.push({
            key: `${option.key}-${effectiveValue}`,
            label: `${option.label} "${effectiveValue}"!`,
            macro: {
              token: type,
              operator: '',
              value: effectiveValue,
            },
          });
        }
      });
    } else if (searchOptions.length < OPTIONS_LIMIT) {
      const filterRequiresInt = SearchFilterValueTypes[type] === 'int';
      const isValueInt = !isNaN(parseInt(currentText));

      if (!filterRequiresInt || (filterRequiresInt && isValueInt)) {
        const effectiveValue = quoteValue(currentText);
        searchOptions.push({
          key: `${option.key}-${effectiveValue}`,
          label: `${option.label} "${effectiveValue}"!`,
          macro: {
            token: type,
            operator: '',
            value: effectiveValue,
          },
        });
      }
    }
  }

  return [...macroOptions, ...searchOptions];
};

const quoteValue = (value: string) => {
  return /\W/.test(value) ? `"${value}"` : value;
};
